import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { ISelectOptions } from 'context';
import { useOutsideClick } from 'services/hooks';

import { Input, DatePicker, Button, ButtonSizes } from 'components/atoms';
import { SelectComponent } from 'atoms/Select/Select';

export interface IMethodistCoursesFilter {
  search: string;
  onSearchChange: (value: string) => void;
  courseOptions: ISelectOptions[];
  selectedCourseId: string;
  onCourseChange: (courseId: string) => void;
  groupOptions: ISelectOptions[];
  selectedGroupId: string;
  onGroupChange: (groupId: string) => void;
  lessonDate: Date | null;
  onLessonDateChange: (date: Date | null) => void;
  onReset: () => void;
  completionFilter?: React.ReactNode;
  className?: string;
}

const EMPTY_OPTION: ISelectOptions = { label: '', value: '' };

export const MethodistCoursesFilter = memo(
  ({
    search,
    onSearchChange,
    courseOptions,
    selectedCourseId,
    onCourseChange,
    groupOptions,
    selectedGroupId,
    onGroupChange,
    lessonDate,
    onLessonDateChange,
    onReset,
    completionFilter,
    className,
  }: IMethodistCoursesFilter) => {
    const { t } = useTranslation();

    const [isCourseOpened, setIsCourseOpened] = useState(false);
    const [isGroupOpened, setIsGroupOpened] = useState(false);

    const closeCourseSelect = useCallback(() => setIsCourseOpened(false), []);
    const closeGroupSelect = useCallback(() => setIsGroupOpened(false), []);

    const courseSelectRef = useOutsideClick(closeCourseSelect);
    const groupSelectRef = useOutsideClick(closeGroupSelect);

    const toggleCourseSelect = useCallback(() => setIsCourseOpened((prev) => !prev), []);
    const toggleGroupSelect = useCallback(() => setIsGroupOpened((prev) => !prev), []);

    const courseSelectOptions = useMemo<ISelectOptions[]>(
      () => [{ value: '', label: t('methodistFilters.courseAll') }, ...courseOptions],
      [courseOptions, t],
    );

    const groupSelectOptions = useMemo<ISelectOptions[]>(
      () => [{ value: '', label: t('methodistFilters.groupAll') }, ...groupOptions],
      [groupOptions, t],
    );

    const selectedCourseOption =
      courseSelectOptions.find((option) => option.value === selectedCourseId) || EMPTY_OPTION;

    const selectedGroupOption =
      groupSelectOptions.find((option) => option.value === selectedGroupId) || EMPTY_OPTION;

    const onSearchInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value),
      [onSearchChange],
    );

    const onCourseOptionClick = useCallback(
      (option: ISelectOptions) => {
        onCourseChange(option.value);
        setIsCourseOpened(false);
      },
      [onCourseChange],
    );

    const onGroupOptionClick = useCallback(
      (option: ISelectOptions) => {
        onGroupChange(option.value);
        setIsGroupOpened(false);
      },
      [onGroupChange],
    );

    return (
      <Wrapper className={className}>
        {completionFilter}
        <Controls>
          <SearchInput
            type="text"
            value={search}
            placeholder={t('methodistFilters.searchPlaceholder')}
            onChange={onSearchInputChange}
          />
          <StyledSelect
            options={courseSelectOptions}
            selectedOption={selectedCourseOption}
            placeholder={t('methodistFilters.course')}
            isOpened={isCourseOpened}
            onSelectClick={toggleCourseSelect}
            onOptionClick={onCourseOptionClick}
            dropdownRef={courseSelectRef}
          />
          <StyledSelect
            options={groupSelectOptions}
            selectedOption={selectedGroupOption}
            placeholder={t('methodistFilters.group')}
            isOpened={isGroupOpened}
            onSelectClick={toggleGroupSelect}
            onOptionClick={onGroupOptionClick}
            dropdownRef={groupSelectRef}
          />
          <DatePickerWrapper>
            <DatePicker
              value={lessonDate}
              placeholder={t('methodistFilters.lessonDate')}
              onChange={onLessonDateChange}
            />
          </DatePickerWrapper>
          <ResetButton variant="text" size={ButtonSizes.sm} sidePaddings={8} onClick={onReset}>
            {t('methodistFilters.reset')}
          </ResetButton>
        </Controls>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  width: 100%;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const SearchInput = styled(Input)`
  flex: 1 1 240px;
  min-width: 200px;
`;

const StyledSelect = styled(SelectComponent)`
  flex: 0 1 220px;
  width: 220px;
`;

const DatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ResetButton = styled(Button)`
  width: auto;
`;
